/**
 * FeaturedReviewsCarousel ✦ iter228 — reusable component
 *
 * Drops onto any page and auto-pulls approved reviews matching the
 * provided filter (on_homepage / on_product_page / featured). Supports
 * two visual variants:
 *   • variant="dark"  — for the editorial walnut homepage
 *   • variant="cream" — for the Heritage cream surfaces (photobooth hire, etc.)
 *
 * Renders nothing if the API returns zero matching reviews — so the
 * carousel never leaves an empty "Loved by" gap on the page until
 * you've assigned at least one review.
 *
 * Props:
 *   filter        "homepage" | "product_page" | "featured" (default "featured")
 *   product       optional product slug — when set + filter="product_page"
 *                 the carousel only shows reviews tagged with that product
 *   variant       "dark" | "cream" (default "cream")
 *   eyebrow       optional override for the eyebrow line
 *   heading       optional override for the section heading
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';

const PALETTES = {
  cream: {
    bg: '#FFFCF6',
    primary: '#4B371E',
    secondary: '#7A5C3E',
    accent: '#9C7C2E',
    accentSoft: '#C9B07A',
    text: '#2E2E2E',
    textDim: '#6B5E4F',
    cardBg: '#FFFFFF',
    cardBorder: 'rgba(122,92,62,0.16)',
    cardShadow: '0 18px 60px rgba(75,55,30,0.12)',
    arrowBg: 'rgba(75,55,30,0.06)',
    sectionDivider: 'rgba(122,92,62,0.10)',
  },
  dark: {
    bg: '#0E0805',
    primary: '#FBEED0',
    secondary: 'rgba(251,238,208,0.72)',
    accent: '#D4AF5E',
    accentSoft: 'rgba(212,170,82,0.42)',
    text: '#FBEED0',
    textDim: 'rgba(251,238,208,0.66)',
    cardBg: 'rgba(20,12,8,0.78)',
    cardBorder: 'rgba(212,170,82,0.22)',
    cardShadow: '0 22px 60px rgba(0,0,0,0.4)',
    arrowBg: 'rgba(212,170,82,0.12)',
    sectionDivider: 'rgba(212,170,82,0.16)',
  },
};

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

export const FeaturedReviewsCarousel = ({
  filter = 'featured',
  product = null,
  variant = 'cream',
  eyebrow,
  heading,
}) => {
  const [reviews, setReviews] = useState([]);
  const scrollerRef = useRef(null);
  const P = PALETTES[variant] || PALETTES.cream;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { limit: 12 };
        if (filter === 'homepage') params.on_homepage = true;
        else if (filter === 'product_page') params.on_product_page = true;
        else params.featured = true;
        if (product) params.product = product;
        const r = await axios.get(`${API}/reviews`, { params });
        if (cancelled) return;
        setReviews(r.data?.reviews || []);
      } catch (e) { /* silent — empty state hides the section */ }
    })();
    return () => { cancelled = true; };
  }, [filter, product]);

  const defaultHeading = useMemo(() => {
    if (filter === 'product_page') return 'Real reviews from real customers';
    if (filter === 'homepage') return 'Loved by couples & businesses across North Wales';
    return 'Words from past EventDrop customers';
  }, [filter]);

  const scrollBy = (dx) => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollBy({ left: dx, behavior: 'smooth' });
    }
  };

  if (reviews.length === 0) return null;

  return (
    <section
      data-testid={`featured-reviews-${variant}-${filter}`}
      style={{
        background: P.bg,
        color: P.text,
        fontFamily: SERIF,
        padding: 'clamp(60px, 8vw, 100px) clamp(20px, 5vw, 60px)',
        borderTop: `1px solid ${P.sectionDivider}`,
        borderBottom: `1px solid ${P.sectionDivider}`,
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          flexWrap: 'wrap', gap: 14, marginBottom: 30,
        }}>
          <div style={{ maxWidth: 720 }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11.5, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: P.secondary, marginBottom: 10,
            }}>
              {eyebrow || '✦ Featured reviews'}
            </div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(26px, 3.8vw, 40px)',
              color: P.primary, margin: 0, letterSpacing: '-0.018em',
              lineHeight: 1.1,
            }}>{heading || defaultHeading}</h2>
          </div>
          <Link to="/reviews" data-testid="featured-reviews-view-all" style={{
            fontFamily: SERIF, fontSize: 13, letterSpacing: '0.06em',
            color: P.accent, textDecoration: 'underline',
            textUnderlineOffset: 4, textDecorationColor: P.accentSoft,
          }}>View all reviews →</Link>
        </div>

        <div style={{ position: 'relative' }}>
          {reviews.length > 1 && (
            <>
              <ArrowBtn dir="left" onClick={() => scrollBy(-360)} P={P} />
              <ArrowBtn dir="right" onClick={() => scrollBy(360)} P={P} />
            </>
          )}
          <div
            ref={scrollerRef}
            data-testid="featured-reviews-scroller"
            style={{
              display: 'flex', gap: 18, overflowX: 'auto', scrollSnapType: 'x mandatory',
              paddingBottom: 6, scrollbarWidth: 'thin',
              scrollbarColor: `${P.accentSoft} transparent`,
            }}
          >
            {reviews.map(r => <CarouselCard key={r.review_id} review={r} P={P} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

const ArrowBtn = ({ dir, onClick, P }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={dir === 'left' ? 'Previous reviews' : 'More reviews'}
    data-testid={`featured-reviews-arrow-${dir}`}
    style={{
      position: 'absolute', top: '50%', transform: 'translateY(-50%)',
      [dir]: -8,
      width: 38, height: 38, borderRadius: '50%',
      background: P.arrowBg, border: `1px solid ${P.accentSoft}`,
      color: P.accent, fontFamily: SERIF, fontSize: 18,
      cursor: 'pointer', zIndex: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'transform 160ms ease, background 160ms ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1.06)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
  >{dir === 'left' ? '‹' : '›'}</button>
);

const CarouselCard = ({ review, P }) => {
  const photo = (review.image_urls || [])[0];
  return (
    <article
      data-testid={`featured-review-card-${review.review_id}`}
      style={{
        flex: '0 0 340px',
        scrollSnapAlign: 'start',
        background: P.cardBg,
        border: `1px solid ${P.cardBorder}`,
        borderRadius: 14,
        padding: '22px 22px 24px',
        boxShadow: P.cardShadow,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ color: P.accent, fontSize: 14, letterSpacing: 2, marginBottom: 12 }}>
        {'★'.repeat(Math.max(0, Math.min(5, review.rating)))}
        <span style={{ color: P.accentSoft }}>
          {'★'.repeat(5 - Math.max(0, Math.min(5, review.rating)))}
        </span>
      </div>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 14, color: P.text, lineHeight: 1.65,
        margin: '0 0 16px', flex: 1,
      }}>"{review.review_text.length > 240
            ? `${review.review_text.slice(0, 240)}…`
            : review.review_text}"</p>
      {photo && (
        <div style={{
          backgroundImage: `url(${mediaUrl(photo)})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          aspectRatio: '4 / 3', borderRadius: 8, marginBottom: 14,
        }} />
      )}
      <div style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: 14,
        color: P.primary, letterSpacing: '-0.005em',
      }}>{review.customer_name}</div>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11.5, color: P.textDim, marginTop: 3,
      }}>
        {[review.event_type, review.venue].filter(Boolean).join(' · ')}
      </div>
    </article>
  );
};

export default FeaturedReviewsCarousel;
